import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';
import { TTipoContaFinanceira } from '../cadastros/ContaBancaria';

export interface IListagem {
  idcaixa: string;
  idconta: string;
  numero: string;
  nome: string;
  dataoperacao: Date;
  historico: string;
  entrada: number;
  saida: number;
  idpareceiro: string;
  idoperacao: string;
  tipo: number;
  datalan: Date;
  // `datalan` é só a data (sem hora) — a hora de verdade de cada lançamento
  // vem daqui, usada pra desempatar a ordem de registros do mesmo dia.
  criadoem: Date;
  status: boolean;
  tipos: number;
  logo: string;
  // Só vêm preenchidos pra tenants com suporte a estorno/sessão de caixa
  // (ver tenantSuportaEstornoCaixa/tenantSuportaSessaoCaixa no backend).
  revertido?: boolean;
  revertidoem?: string | null;
  numerosessao?: string | null;
}
export interface IResultado{
  saldoanterior: number;
  credito: number;
  debito: number;
  saldo: number;
  valorcheque: number;
  saldoreal: number;
  // Saldo por forma de pagamento (Dinheiro, PIX, Cartão, Cheque...) restrito
  // ao período pesquisado — mesma base de credito/debito, não inclui saldo
  // de outros dias.
  porFormaPagamento: { categoria: string; valor: number }[];
  // Entrada e saída por forma de pagamento somadas separadamente (não o
  // líquido), restritas ao período pesquisado — mesma base de credito/debito.
  porFormaPagamentoEntrada: { categoria: string; valor: number }[];
  porFormaPagamentoSaida: { categoria: string; valor: number }[];
}
interface IFecharCaixa {
  conta: string;
  datafechar: Date;
}
interface IAbrirCaixa {
  conta: string;
  dataabrir: Date;
}
export interface IVoltarLancamento {
  idoperacao: string;
  datacaixa: Date;
  idconta: string;
  tipos: number;
}
export interface IRegistroPorOperacao {
  idcaixa: string;
  datalan: Date;
  criadoem: Date;
  numero: string;
  historico: string;
  entrada: number;
  saida: number;
  nomeConta: string;
}
export interface ITransferirRecebivel {
  idcontarecebivel: string;
  idcontadestino: string;
  valorbruto: number;
  valorliquido: number;
  operadora?: string;
  datalan: Date;
}
export interface IResultadoTransferirRecebivel {
  idcontaRecebivel: string;
  idcontaDestino: string;
  valorBruto: number;
  valorLiquido: number;
  taxa: number;
  datalan: Date;
  historico: string;
  numero: number;
}
type TotalCount = {
    resultado: IResultado,
    lan: IListagem[];
    totalCount: number;
}
interface ErrorResponse {
  errors: {
    default: string;
  };
}
const getAll = async (tipo: string, conta: string, datainicial = '', datafinal=''): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = `/caixa?limit=${300}&tipo=${tipo}&conta=${conta}&datainicial=${datainicial}&datafinal=${datafinal}`;
    const { data, headers } = await Api.get(urlRelativa); 

    if (data) {
      return {
        resultado: data[0],
        lan: data[1],
        totalCount: Number(headers['x-total-count'] ),
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

export interface ITotalPorTipo {
  tipo: TTipoContaFinanceira;
  saldo: number;
}
const getTotaisPorTipo = async (): Promise<ITotalPorTipo[] | Error> => {
  try {
    const { data } = await Api.get('/caixa/totaisportipo');
    if (data) {
      return (data as { tipo: TTipoContaFinanceira; saldo: number | string }[]).map((item) => ({
        tipo: item.tipo,
        saldo: Number(item.saldo || 0),
      }));
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

export interface ISaldoPorConta {
  idconta: string;
  titular: string;
  numeroconta: string | null;
  tipo: TTipoContaFinanceira;
  saldo: number;
}
export interface IPorFormaPagamento {
  categoria: string;
  valor: number;
}
// Totais BRUTOS recebidos no período (só entrada, sem descontar saída) — ver
// SaldosPorConta.ts (backend) pro critério exato de cada campo.
export interface ITotaisRecebidos {
  credito: number;
  debito: number;
  pixDinamico: number;
  dinheiro: number;
  contasBancarias: number;
}
export interface IResumoSaldosPorConta {
  porConta: ISaldoPorConta[];
  recebiveisPorFormaPagamento: IPorFormaPagamento[];
  totaisRecebidos: ITotaisRecebidos;
}
const getSaldosPorConta = async (datainicial: string, datafinal: string): Promise<IResumoSaldosPorConta | Error> => {
  try {
    const { data } = await Api.get(`/caixa/saldosporconta?datainicial=${datainicial}&datafinal=${datafinal}`);
    if (data) {
      const resultado = data as IResumoSaldosPorConta;
      const totais = resultado.totaisRecebidos;
      return {
        porConta: resultado.porConta.map((item) => ({ ...item, saldo: Number(item.saldo || 0) })),
        recebiveisPorFormaPagamento: resultado.recebiveisPorFormaPagamento.map((item) => ({ ...item, valor: Number(item.valor || 0) })),
        totaisRecebidos: {
          credito: Number(totais?.credito || 0),
          debito: Number(totais?.debito || 0),
          pixDinamico: Number(totais?.pixDinamico || 0),
          dinheiro: Number(totais?.dinheiro || 0),
          contasBancarias: Number(totais?.contasBancarias || 0),
        },
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const FecharCaixa = async (dados: IFecharCaixa): Promise<string | Error> => {
  try {

    const { data } = await Api.post<IFecharCaixa>('/fecharconciliacao',  dados);
  
    if (Object.keys(data).length === 0) {
      return 'ok';
    }
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

const AbrirCaixa = async (dados: IAbrirCaixa): Promise<string | Error> => {
  try {
    const { data } = await Api.post<IAbrirCaixa>('/abrirconciliacao',  dados);
  
    if (Object.keys(data).length === 0) {
      return 'ok';
    }
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
// Uma operação pode ter linhas de caixa em mais de uma conta (ex.: venda
// paga parte em Dinheiro no caixa do PDV, parte em Cartão na conta de
// recebíveis) — usado pelo diálogo de confirmação do "Voltar Lançamento"
// pra mostrar o quadro completo, não só o que está na conta pesquisada na
// tela. O backend já filtra fora o que já foi revertido antes.
const getPorOperacao = async (idoperacao: string): Promise<IRegistroPorOperacao[] | Error> => {
  try {
    const { data } = await Api.get(`/caixa/poroperacao?idoperacao=${idoperacao}`);
    return data as IRegistroPorOperacao[];
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const VoltarLancamento = async (dados: IVoltarLancamento): Promise<string | Error> => {
  try {
    const { data } = await Api.post<IAbrirCaixa>('/voltarlancamento',  dados);
    if (Object.keys(data).length === 0) {
      return 'ok';
    }
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const TransferirRecebivel = async (dados: ITransferirRecebivel): Promise<IResultadoTransferirRecebivel | Error> => {
  try {
    const { data } = await Api.post<ITransferirRecebivel>('/transferirrecebivel', dados);
    return data as unknown as IResultadoTransferirRecebivel;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};

export const CaixaConstasService = {
  getAll,
  getTotaisPorTipo,
  getSaldosPorConta,
  getPorOperacao,
  FecharCaixa,
  AbrirCaixa,
  VoltarLancamento,
  TransferirRecebivel,
};